import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import { fadeInUp, staggerContainer, staggerItem } from '../utils/animations';

const Residential = () => {
  const navigate = useNavigate();

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-20 hero-gradient">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-dark mb-6">
              Residential <span className="text-primary-brown">Beauty</span>
            </h1>
            <p className="text-xl text-dark-light leading-relaxed">
              Transform your home into a masterpiece with our premium residential services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-accent-cream">
        <div className="container mx-auto px-4">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              { id: 1, image: '/images/Residential 1.PNG', alt: 'Residential Transformation 1' },
              { id: 2, image: '/images/Residential 2.PNG', alt: 'Residential Transformation 2' },
              { id: 3, image: '/images/Residential 3.PNG', alt: 'Residential Transformation 3' }
            ].map((item) => (
              <motion.div
                key={item.id}
                className="aspect-[3/4] rounded-lg overflow-hidden shadow-lg"
                variants={staggerItem}
              >
                <img src={item.image} alt={item.alt} className="w-full h-full object-cover" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Service 1: Interior Painting & Decorative Finishes */}
      <section id="paint-coatings" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-4">
          <SectionTitle
            title="Interior Painting & Decorative Finishes"
            subtitle="Service 01"
            align="center"
          />

          <motion.div
            className="max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-lg text-dark-light leading-relaxed text-center mb-16">
              Created to shape comfortable, cohesive living spaces with finishes that match personal style and everyday use.
            </p>

            {/* Bento Grid Layout */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
              {/* First Subcategory - Spans 4 columns */}
              <motion.div
                className="md:col-span-4 bg-accent-cream p-10 rounded-lg shadow-xl"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <div className="flex items-start gap-6">
                  <div className="w-20 h-20 bg-primary-brown rounded-2xl flex items-center justify-center flex-shrink-0">
                    <span className="text-4xl text-white font-serif">1</span>
                  </div>
                  <div>
                    <h4 className="text-2xl font-serif text-dark mb-4">Smooth and Refined Wall Paint Applications</h4>
                    <p className="text-dark-light leading-relaxed text-lg">
                      We apply high-quality paints with methods that ensure a smooth, polished, and consistent finish. The result is a clean interior surface with even color depth, no patchiness, and a refined overall appearance that elevates the space.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Visual Element - Spans 2 columns */}
              <motion.div
                className="md:col-span-2 bg-gradient-to-br from-primary-beige to-primary-tan rounded-lg shadow-xl min-h-[200px] overflow-hidden"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.15 }}
              >
                <img src="/images/Residential Service 1 Sub 1.png" alt="Smooth and Refined Wall Paint Applications" className="w-full h-full object-cover" />
              </motion.div>

              {/* Visual Element - Spans 2 columns */}
              <motion.div
                className="md:col-span-2 bg-gradient-to-br from-primary-tan to-accent-cream rounded-lg shadow-xl min-h-[200px] overflow-hidden"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <img src="/images/Residential Service 1 Sub 2.png" alt="Smooth and Refined Wall Paint Applications" className="w-full h-full object-cover" />
              </motion.div>

              {/* Second Subcategory - Spans 4 columns */}
              <motion.div
                className="md:col-span-4 bg-primary-beige p-10 rounded-lg shadow-xl"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.25 }}
              >
                <div className="flex items-start gap-6">
                  <div className="w-20 h-20 bg-primary-brown rounded-2xl flex items-center justify-center flex-shrink-0">
                    <span className="text-4xl text-white font-serif">2</span>
                  </div>
                  <div>
                    <h4 className="text-2xl font-serif text-dark mb-4">Textured and Feature Walls</h4>
                    <p className="text-dark-light leading-relaxed text-lg">
                    Textured and feature walls offer a unique way to define zones within open-plan interiors. By using distinct patterns, finishes, or tonal contrasts, they help create visual separation between areas—such as living, dining, or workspace zones—without the need for physical partitions. This makes even a minimal layout feel thoughtfully structured and aesthetically engaging.

In addition to enhancing space planning, these walls also serve as a canvas for creativity. Specialty finishes like concrete effects, metallic sheens, stone textures, or artistic coatings can transform ordinary surfaces into standout design statements. With the right finish, a wall becomes more than just a backdrop—it adds character, sets the mood, and elevates the overall atmosphere of the interior.

When thoughtfully chosen, textured and feature walls also enhance lighting effects within a space. Their depth and pattern interact with natural and artificial light to create shadows, highlights, and reflections that change throughout the day. This subtle play of light adds warmth and movement, making the wall feel alive and giving the interior a dynamic, ever-evolving appeal.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Third Subcategory - Full width */}
              <motion.div
                className="md:col-span-6 bg-gradient-to-r from-primary-tan via-accent-cream to-primary-beige p-12 rounded-lg shadow-2xl"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-start gap-6 max-w-5xl mx-auto">
                  <div className="w-20 h-20 bg-primary-brown rounded-2xl flex items-center justify-center flex-shrink-0">
                    <span className="text-4xl text-white font-serif">3</span>
                  </div>
                  <div>
                    <h4 className="text-3xl font-serif text-dark mb-4">Color Selection and Design Guidance</h4>
                    <p className="text-dark-light leading-relaxed text-lg">
                      Choosing the right colors is essential for harmony and flow. We offer guidance to help select tones that match lighting, furnishings, and the overall mood of the room. The outcome is a thoughtfully balanced palette that feels intentional and cohesive.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service 2: Premium Floor & Surface Treatments */}
      <section id="texture-finishes" className="py-20 bg-accent-cream scroll-mt-24">
        <div className="container mx-auto px-4">
          <SectionTitle
            title="Premium Floor & Surface Treatments"
            subtitle="Service 02"
            align="center"
          />

          <motion.div
            className="max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-lg text-dark-light leading-relaxed text-center mb-16">
              Designed for durability, comfort, and a clean, modern look that holds up to daily life.
            </p>

            {/* Offset Card Layout */}
            <div className="space-y-8">
              <motion.div
                className="grid md:grid-cols-2 gap-8 items-stretch"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <div className="bg-white p-10 rounded-2xl shadow-2xl flex flex-col">
                  <div className="w-16 h-16 bg-primary-brown rounded-xl flex items-center justify-center mb-6">
                    <span className="text-3xl text-white">1</span>
                  </div>
                  <h4 className="text-2xl font-serif text-dark mb-4">Seamless Floor Coatings for Living Areas</h4>
                  <p className="text-dark-light leading-relaxed">
                  Seamless floor coatings create smooth, joint-free surfaces that enhance both the look and practicality of living spaces. These coatings provide a clean, modern finish that is comfortable underfoot, easy to maintain, and visually cohesive across rooms.

Beyond aesthetics, seamless floor coatings are highly durable and resistant to everyday wear, spills, and household activities. Their non-porous surface prevents dust, dirt, and moisture from settling into cracks or joints, making them a hygienic choice for families and busy environments. With minimal effort, the floors retain their appearance and performance for years.

These coatings also offer versatile design possibilities to suit different interior styles. From soft matte finishes to glossy contemporary looks, they can be customized in subtle tones, earthy textures, or bold modern shades. This flexibility allows homeowners to achieve a cohesive floor finish that complements furniture, wall treatments, and decor elements effortlessly.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-primary-beige to-primary-tan rounded-2xl shadow-xl overflow-hidden">
                <img src="/images/Residential Service 2 Sub 1.png" alt="Smooth and Refined Wall Paint Applications" className="w-full h-full object-cover" />
                </div>
              </motion.div>

              <motion.div
                className="grid md:grid-cols-2 gap-8 items-stretch"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <div className="bg-gradient-to-br from-accent-cream to-primary-beige rounded-2xl shadow-xl md:order-1 overflow-hidden">
                  <img src="/images/Residential Service 2 Sub 2.png" alt="Protective and Easy-to-Clean Surface Layers" className="w-full h-full object-cover" />
                </div>
                <div className="bg-white p-10 rounded-2xl shadow-2xl md:order-2 flex flex-col">
                  <div className="w-16 h-16 bg-primary-brown rounded-xl flex items-center justify-center mb-6">
                    <span className="text-3xl text-white">2</span>
                  </div>
                  <h4 className="text-2xl font-serif text-dark mb-4">Protective and Easy-to-Clean Surface Layers</h4>
                  <p className="text-dark-light leading-relaxed">
                  These floor systems include protective layers that shield surfaces from daily wear, spills, and stains. The result is a durable, low-maintenance surface that retains its appearance over time and simplifies everyday cleaning.

Protective surface layers also add a barrier against scratches, chemical exposure, and abrasion caused by foot traffic or moving furniture. This makes them a practical solution for homes, offices, and commercial interiors where durability is essential. Even in high-use areas, the finish stays intact, preserving both the functionality and aesthetics of the space.

In addition to performance benefits, these coatings can be tailored to different textures and sheens, offering customization without compromising strength. Whether the preference is a soft matte look or a sleek glossy finish, the protective layer enhances visual appeal while maintaining effortless cleanability. This balance of style and practicality makes it an ideal choice for design-focused yet busy environments.
                  </p>
                </div>
              </motion.div>

              <motion.div
                className="bg-gradient-to-r from-white via-primary-beige to-white p-12 rounded-2xl shadow-2xl"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <div className="max-w-4xl mx-auto">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-primary-brown rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-3xl text-white">3</span>
                    </div>
                    <div>
                      <h4 className="text-2xl font-serif text-dark mb-4">Customized Finishes for Kitchens, Lounges, and Bedrooms</h4>
                      <p className="text-dark-light leading-relaxed text-lg">
                        We tailor floor finishes to suit the function and aesthetic of each room. Whether the goal is a warm and welcoming living space or a sleek contemporary kitchen, we adjust texture, tone, and finish to match the interior style and lifestyle needs.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service 3: Exterior Coating & Home Protection */}
      <section id="interior-exterior" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-4">
          <SectionTitle
            title="Exterior Coating & Home Protection"
            subtitle="Service 03"
            align="center"
          />

          <motion.div
            className="max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-lg text-dark-light leading-relaxed text-center mb-16">
              Focused on maintaining the home's appearance while protecting it from environmental wear over time.
            </p>

            {/* Modern Card Grid */}
            <div className="grid md:grid-cols-2 gap-8">
              <motion.div
                className="bg-accent-cream p-10 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <div className="w-16 h-16 bg-primary-brown rounded-xl flex items-center justify-center mb-6">
                  <span className="text-3xl text-white">1</span>
                </div>
                <h4 className="text-2xl font-serif text-dark mb-4">Weather-Resistant Exterior Coating Systems</h4>
                <p className="text-dark-light leading-relaxed">
                  Our exterior coating systems are designed to protect home surfaces from sunlight, rain, humidity, and everyday environmental exposure. These coatings help maintain color stability, prevent surface deterioration, and keep the exterior looking fresh and well-kept over time.
                </p>
              </motion.div>

              <motion.div
                className="bg-primary-beige p-10 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <div className="w-16 h-16 bg-primary-brown rounded-xl flex items-center justify-center mb-6">
                  <span className="text-3xl text-white">2</span>
                </div>
                <h4 className="text-2xl font-serif text-dark mb-4">Façade Refresh and Restoration</h4>
                <p className="text-dark-light leading-relaxed">
                  We restore worn or faded exterior surfaces to bring back a clean, well-maintained appearance. This includes surface preparation, repairs, and refinishing to enhance curb appeal without altering the character of the home. The result is a renewed façade that looks cared for and cohesive.
                </p>
              </motion.div>

              <motion.div
                className="md:col-span-2 bg-gradient-to-r from-primary-tan to-accent-cream p-12 rounded-2xl shadow-2xl"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <div className="max-w-4xl mx-auto">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-primary-brown rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-3xl text-white">3</span>
                    </div>
                    <div>
                      <h4 className="text-3xl font-serif text-dark mb-4">UV and Moisture-Protective Layers</h4>
                      <p className="text-dark-light leading-relaxed text-lg">
                        These protective layers form a durable barrier that guards your home's exterior against fading, cracking, and moisture damage. By shielding surfaces from harsh sunlight and changing weather, they help preserve structural integrity and reduce the need for frequent repainting or repairs.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <SectionTitle title="What Homeowners Say" align="center" />

          <motion.div
            className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div
              className="bg-primary-beige p-8 rounded-lg"
              variants={staggerItem}
            >
              <p className="text-dark italic mb-4">
                "CraftCoat Solutions transformed our home beautifully. The attention to
                detail and professionalism were outstanding!"
              </p>
              <p className="text-primary-brown font-medium">- Happy Homeowner</p>
            </motion.div>

            <motion.div
              className="bg-accent-cream p-8 rounded-lg"
              variants={staggerItem}
            >
              <p className="text-dark italic mb-4">
                "Exceptional quality and service. They turned our vision into reality
                with precision and care."
              </p>
              <p className="text-primary-brown font-medium">- Satisfied Client</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-brown text-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-serif mb-6">
              Ready to Transform Your Home?
            </h2>
            <p className="text-xl mb-8 text-primary-beige">
              Let's bring your vision to life with colors that inspire.
            </p>
            <Button variant="secondary" size="large" onClick={() => navigate('/contact')}>
              Contact Us
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Residential;
